import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DialogFooter } from '@/components/ui/dialog';
import { ArrowRight } from 'lucide-react';
import { JobFormData, employmentTypes, experienceLevels } from './types';
import { supabase } from '@/integrations/supabase/client';
import { ContractTemplate } from '@/types';

interface JobFormProps {
  formData: JobFormData;
  setFormData: React.Dispatch<React.SetStateAction<JobFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
  businessId: string;
}

const JobForm: React.FC<JobFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isEditing,
  businessId
}) => {
  const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (formData.requires_contract) {
      fetchContractTemplates();
    }
  }, [formData.requires_contract, businessId]);

  const fetchContractTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data, error } = await supabase
        .from('contract_templates')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setContractTemplates(data || []);
    } catch (error) {
      console.error('Error fetching contract templates:', error);
      setContractTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Job Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Sales Associate, Kitchen Staff"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="employment_type">Employment Type</Label>
          <Select 
            value={formData.employment_type} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, employment_type: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {employmentTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="experience_level">Experience Level</Label>
          <Select 
            value={formData.experience_level} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, experience_level: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {experienceLevels.map(level => (
                <SelectItem key={level.value} value={level.value}>
                  {level.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
          placeholder="e.g., Downtown Location, Remote"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="salary_min">Min Salary (Annual)</Label>
          <Input
            id="salary_min"
            type="number"
            value={formData.salary_min}
            onChange={(e) => setFormData(prev => ({ ...prev, salary_min: e.target.value }))}
            placeholder="30000"
          />
        </div>
        <div>
          <Label htmlFor="salary_max">Max Salary (Annual)</Label>
          <Input
            id="salary_max"
            type="number"
            value={formData.salary_max}
            onChange={(e) => setFormData(prev => ({ ...prev, salary_max: e.target.value }))}
            placeholder="50000"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hourly_rate_min">Min Hourly Rate</Label>
          <Input
            id="hourly_rate_min"
            type="number"
            step="0.01"
            value={formData.hourly_rate_min}
            onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate_min: e.target.value }))}
            placeholder="15.00"
          />
        </div>
        <div>
          <Label htmlFor="hourly_rate_max">Max Hourly Rate</Label>
          <Input
            id="hourly_rate_max"
            type="number"
            step="0.01"
            value={formData.hourly_rate_max}
            onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate_max: e.target.value }))}
            placeholder="25.00"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="expires_at">Expiration Date (Optional)</Label>
        <Input
          id="expires_at"
          type="date"
          value={formData.expires_at}
          onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
        />
      </div>

      <div>
        <Label htmlFor="description">Job Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe the role, responsibilities, and what makes this position great..."
          rows={4}
        />
      </div>

      <div>
        <Label htmlFor="requirements">Requirements</Label>
        <Textarea
          id="requirements"
          value={formData.requirements}
          onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
          placeholder="List the skills, experience, and qualifications needed..."
          rows={4}
        />
      </div>

      <div>
        <Label htmlFor="benefits">Benefits</Label>
        <Textarea
          id="benefits"
          value={formData.benefits}
          onChange={(e) => setFormData(prev => ({ ...prev, benefits: e.target.value }))}
          placeholder="Health insurance, paid time off, flexible schedule, etc..."
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="schedule_requirements">Schedule & Availability Requirements</Label>
        <Textarea
          id="schedule_requirements"
          value={formData.schedule_requirements}
          onChange={(e) => setFormData(prev => ({ ...prev, schedule_requirements: e.target.value }))}
          placeholder="Monday-Friday 9-5, weekends required, flexible hours, etc..."
          rows={3}
        />
      </div>

      {/* Contract Management Section */}
      <div className="border-t pt-4 space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="requires_contract"
            checked={formData.requires_contract}
            onCheckedChange={(checked) => setFormData(prev => ({ 
              ...prev, 
              requires_contract: checked,
              default_contract_template_id: checked ? prev.default_contract_template_id : '',
              contract_auto_generate: checked ? prev.contract_auto_generate : false
            }))}
          />
          <Label htmlFor="requires_contract">This position requires a contract</Label>
        </div>

        {formData.requires_contract && (
          <>
            <div>
              <Label htmlFor="contract_template">Contract Template</Label>
              <Select 
                value={formData.default_contract_template_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, default_contract_template_id: value }))}
                disabled={loadingTemplates}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingTemplates ? "Loading templates..." : "Select a contract template"} />
                </SelectTrigger>
                <SelectContent>
                  {contractTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.employment_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contractTemplates.length === 0 && !loadingTemplates && (
                <p className="text-sm text-muted-foreground mt-1">
                  No contract templates found. Create templates in Employee Management → Contracts.
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="contract_auto_generate"
                checked={formData.contract_auto_generate}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, contract_auto_generate: checked }))}
              />
              <Label htmlFor="contract_auto_generate">Auto-generate contract when candidate is hired</Label>
            </div>
          </>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Next: Customize Application Form
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </DialogFooter>
    </form>
  );
};

export default JobForm;